// Phase 29 — Compare page.
//
// Flow:
//   1. Read URL ?ids=<csv>, ?as_user_id=<uuid>
//   2. Load own deals (or target user's deals if admin + as_user_id given)
//   3. Render picker chips. ≥2 selected → fetch /api/compare/deals and show table.
//   4. Export bar at top of the table for xlsx/pdf download.
//
// Free tier UX: caps the picker at 2 selected with a sticky upsell banner if
// the user tries to add a 3rd.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Download, FileSpreadsheet, FileText, ArrowLeft, Crown, Search, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import CompareTable from "@/components/compare/CompareTable";
import AIInsightPanel from "@/components/compare/AIInsightPanel";

const FREE_MAX = 2;
const PRO_MAX = 10;

function ChipDeal({ deal, selected, disabled, onToggle }) {
  return (
    <button
      type="button"
      disabled={disabled && !selected}
      onClick={onToggle}
      data-testid={`compare-chip-${deal.id}`}
      className={`group flex max-w-xs items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-all ${
        selected
          ? "border-indigo-500 bg-indigo-50 text-indigo-900"
          : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300"
      } ${disabled && !selected ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      <span className="truncate font-medium">{deal.property?.address || "(no address)"}</span>
      <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        {(deal.deal_type || "—").slice(0, 5)}
      </span>
      {selected && <X className="h-3 w-3 text-indigo-600" />}
    </button>
  );
}

export default function Compare() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();

  const isPro = ["pro", "premium", "enterprise"].includes((user?.subscription_plan || "free").toLowerCase()) || !!user?.is_admin;
  const isAdmin = !!user?.is_admin;
  const limit = isPro ? PRO_MAX : FREE_MAX;

  const [asUserId, setAsUserId] = useState(params.get("as_user_id") || "");
  const [asUserList, setAsUserList] = useState([]); // for admin View-as dropdown
  const [deals, setDeals] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(true);
  const [query, setQuery] = useState("");

  // selection state — initialise from URL
  const initialSelected = useMemo(() => {
    const ids = (params.get("ids") || "").split(",").map((s) => s.trim()).filter(Boolean);
    return new Set(ids);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [selected, setSelected] = useState(initialSelected);

  const [payload, setPayload] = useState(null);
  const [loadingPayload, setLoadingPayload] = useState(false);
  const [paywallHit, setPaywallHit] = useState(false);
  const [exporting, setExporting] = useState(null); // "xlsx" | "pdf"

  // ── Load deals (for picker)
  const loadDeals = useCallback(() => {
    setLoadingDeals(true);
    let url = "/deals";
    if (asUserId && isAdmin) {
      url = `/admin/deals?user_id=${asUserId}&limit=100`;
    }
    api.get(url)
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data?.items || []);
        setDeals(items);
      })
      .catch(() => setDeals([]))
      .finally(() => setLoadingDeals(false));
  }, [asUserId, isAdmin]);

  useEffect(() => { loadDeals(); }, [loadDeals]);

  // ── For admins: load a small list of users for the "View as" dropdown
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/users?limit=25&intel=false")
      .then(({ data }) => setAsUserList(data?.items || []))
      .catch(() => {});
  }, [isAdmin]);

  // ── Fetch comparison payload whenever selection changes (debounced through state)
  useEffect(() => {
    setPaywallHit(false);
    if (selected.size < 2) {
      setPayload(null);
      return;
    }
    const ids = Array.from(selected).slice(0, limit);
    setLoadingPayload(true);
    const url = asUserId && isAdmin ? `/compare/deals?as_user_id=${asUserId}` : "/compare/deals";
    api.post(url, { deal_ids: ids })
      .then(({ data }) => setPayload(data))
      .catch((e) => {
        if (e?.response?.status === 402) {
          setPaywallHit(true);
          setPayload(null);
        } else {
          toast.error(e?.response?.data?.detail || e.message || "Failed to load comparison");
        }
      })
      .finally(() => setLoadingPayload(false));
  }, [selected, asUserId, isAdmin, limit]);

  // ── Mirror selection into URL
  useEffect(() => {
    const ids = Array.from(selected);
    const p = new URLSearchParams();
    if (ids.length) p.set("ids", ids.join(","));
    if (asUserId) p.set("as_user_id", asUserId);
    setParams(p, { replace: true });
  }, [selected, asUserId, setParams]);

  const toggleDeal = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= limit) {
          if (!isPro) {
            // Free tier: hard cap at 2 but show banner
            toast.warning(`Free tier limited to ${FREE_MAX} deals. Upgrade to compare up to ${PRO_MAX}.`);
            return next;
          }
          toast.warning(`Maximum ${limit} deals per comparison`);
          return next;
        }
        next.add(id);
      }
      return next;
    });
  };

  const clearAll = () => setSelected(new Set());

  const filtered = deals.filter((d) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (d.property?.address || "").toLowerCase().includes(q) ||
      (d.property?.city || "").toLowerCase().includes(q) ||
      (d.deal_type || "").toLowerCase().includes(q)
    );
  });

  const doExport = async (fmt) => {
    if (selected.size < 2) {
      toast.error("Pick at least 2 deals first");
      return;
    }
    setExporting(fmt);
    try {
      const ids = Array.from(selected).slice(0, limit);
      const path = fmt === "xlsx" ? "/compare/deals/export.xlsx" : "/compare/deals/export.pdf";
      const url = asUserId && isAdmin ? `${path}?as_user_id=${asUserId}` : path;
      const resp = await api.post(url, { deal_ids: ids }, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      // Extract filename from Content-Disposition or fall back
      const cd = resp.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `runidea-comparison.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success(`${fmt.toUpperCase()} download started`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Export failed";
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Export failed"));
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              className="mb-2 -ms-3 text-slate-600"
              onClick={() => navigate("/dashboard")}
              data-testid="compare-back-btn"
            >
              <ArrowLeft className="me-1 h-4 w-4" /> Back to dashboard
            </Button>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Compare Properties</h1>
            <p className="mt-1 text-sm text-slate-500">
              Pick {isPro ? `up to ${PRO_MAX}` : `up to ${FREE_MAX}`} deals to see every metric side-by-side. Export to Excel or PDF.
            </p>
          </div>
          {!isPro && (
            <Link
              to="/settings"
              className="flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-200"
              data-testid="compare-upgrade-pill"
            >
              <Crown className="h-3 w-3" /> Free tier
            </Link>
          )}
        </div>

        {/* Admin "View as" dropdown */}
        {isAdmin && (
          <div className="mb-4 flex items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50/60 p-3" data-testid="compare-view-as">
            <span className="text-xs font-bold uppercase tracking-wider text-indigo-700">View as</span>
            <Select value={asUserId || "__self__"} onValueChange={(v) => { setAsUserId(v === "__self__" ? "" : v); clearAll(); }}>
              <SelectTrigger className="h-8 max-w-xs text-sm" data-testid="compare-view-as-select">
                <SelectValue placeholder="Yourself" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__self__">Yourself (admin)</SelectItem>
                {asUserList.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {asUserId && (
              <span className="text-xs text-slate-500" data-testid="compare-view-as-status">Showing this user's deals only.</span>
            )}
          </div>
        )}

        {/* Picker */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">
              Pick deals ({selected.size}/{limit})
            </h2>
            <div className="flex items-center gap-2">
              {selected.size > 0 && (
                <Button size="sm" variant="ghost" onClick={clearAll} data-testid="compare-clear-btn">
                  <X className="me-1 h-3.5 w-3.5" /> Clear
                </Button>
              )}
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter…"
                  className="h-8 w-48 pl-8 text-sm"
                  data-testid="compare-picker-search"
                />
              </div>
            </div>
          </div>

          {loadingDeals ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading your deals…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-6 text-sm text-slate-400" data-testid="compare-picker-empty">
              No deals found. <Link to="/deals/new" className="text-indigo-600 hover:underline">Create one</Link>.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="compare-picker">
              {filtered.map((d) => (
                <ChipDeal
                  key={d.id}
                  deal={d}
                  selected={selected.has(d.id)}
                  disabled={!selected.has(d.id) && selected.size >= limit}
                  onToggle={() => toggleDeal(d.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Paywall banner */}
        {paywallHit && (
          <div className="mt-4 flex items-center justify-between rounded-xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="compare-paywall-banner">
            <div className="text-sm text-amber-900">
              <span className="font-bold">Upgrade to Pro</span> to compare up to {PRO_MAX} deals at once and unlock sensitivity grids + historical IRR breakdowns.
            </div>
            <Link to="/settings" className="rounded-md bg-amber-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-800">
              Upgrade
            </Link>
          </div>
        )}

        {/* Export bar + table */}
        {selected.size >= 2 && (
          <>
            {/* Phase 35d — AI Insight panel: investor-prose narrative at the
                top, above the raw metrics table. Only renders when the
                comparison payload has loaded so it lines up with the table. */}
            {payload && (
              <AIInsightPanel
                dealIds={Array.from(selected).slice(0, limit)}
                dealsByID={Object.fromEntries(deals.map((d) => [d.id, d]))}
              />
            )}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="text-xs text-slate-500">
                {loadingPayload ? "Building comparison…" : payload ? `${payload.meta.deal_count} deals · generated ${payload.meta.generated_at}` : ""}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => doExport("xlsx")}
                  disabled={!payload || exporting === "xlsx"}
                  data-testid="compare-export-xlsx"
                >
                  {exporting === "xlsx" ? <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="me-1.5 h-3.5 w-3.5" />}
                  Export Excel (.xlsx)
                </Button>
                <Button
                  size="sm"
                  onClick={() => doExport("pdf")}
                  disabled={!payload || exporting === "pdf"}
                  data-testid="compare-export-pdf"
                >
                  {exporting === "pdf" ? <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" /> : <FileText className="me-1.5 h-3.5 w-3.5" />}
                  Export PDF
                </Button>
              </div>
            </div>

            <div className="mt-4">
              {loadingPayload ? (
                <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading comparison…
                </div>
              ) : payload ? (
                <CompareTable payload={payload} />
              ) : null}
            </div>
          </>
        )}

        {/* Empty-state when 0-1 selected */}
        {selected.size < 2 && !paywallHit && (
          <div className="mt-8 rounded-2xl border-2 border-dashed border-slate-200 bg-white py-12 text-center" data-testid="compare-empty-state">
            <Download className="mx-auto mb-3 h-8 w-8 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700">Pick at least 2 deals to compare</h3>
            <p className="mt-1 text-sm text-slate-500">
              Side-by-side table renders here. Export buttons unlock once you've picked 2 or more.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
